import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green, grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { List, Tooltip, IconButton } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";
import GroupIcon from '@material-ui/icons/Group';
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketModal from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { HighlightOff, Replay, SwapHoriz, Check, ArchiveOutlined, CheckCircleOutline, Shuffle } from "@material-ui/icons";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { Avatar, Badge, ListItemAvatar, ListItem, ListItemSecondaryAction, ListItemText, Typography } from "@material-ui/core";
import CloseTicketModal from "../CloseTicketModal";
import { useBulkSelection } from "../../context/BulkSelection/BulkSelectionContext";
import { useScrollPosition } from "../../context/ScrollPosition/ScrollPositionContext";

// Utilitários para fontes de contato
import { getSourceIcon } from "../../utils/contactSourceUtils";



const useStyles = makeStyles((theme) => ({
    ticket: {
        position: "relative",
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: "8px",
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1),
        transition: "box-shadow 0.3s ease-in-out",
        "&:hover": {
            boxShadow: `0 2px 8px rgba(0, 0, 0, 0.1)`,
        }
    },
    pendingTicket: {
        cursor: "unset",
    },
    queueTag: {
        background: "#FCFCFC",
        color: "#000",
        marginRight: 1,
        padding: 1,
        fontWeight: 'bold',
        borderRadius: 3,
        fontSize: "0.5em",
        whiteSpace: "nowrap"
    },
    noTicketsDiv: {
        display: "flex",
        height: "100px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
    newMessagesCount: {
        justifySelf: "flex-end",
        textAlign: "right",
        position: "relative",
        top: 0,
        color: "green",
        fontWeight: "bold",
        marginRight: "10px",
        borderRadius: 0,
    },
    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },
    connectionTag: {
        background: "green",
        color: "#FFF",
        marginRight: 1,
        padding: 1,
        fontWeight: 'bold',
        borderRadius: 3,
        fontSize: "0.6em",
    },
    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },
    contactNameWrapper: {
        display: "flex",
        justifyContent: "space-between",
        marginLeft: "5px",
        fontWeight: "bold",
        color: theme.mode === 'light' ? "black" : "white",
    },

    lastMessageTime: {
        justifySelf: "flex-end",
        textAlign: "right",
        position: "relative",
        top: -30,
        marginRight: "1px",
        color: theme.mode === 'light' ? "black" : grey[400],
    },
    lastMessageTimeUnread: {
        justifySelf: "flex-end",
        textAlign: "right",
        position: "relative",
        top: -30,
        color: "green",
        fontWeight: "bold",
        marginRight: "1px",
    },
    closedBadge: {
        alignSelf: "center",
        justifySelf: "flex-end",
        marginRight: 32,
        marginLeft: "auto",
    },
    contactLastMessage: {
        paddingRight: "0%",
        marginLeft: "5px",
        color: theme.mode === 'light' ? "black" : grey[400],
    },
    contactLastMessageUnread: {
        paddingRight: 20,
        fontWeight: "bold",
        color: theme.mode === 'light' ? "black" : grey[400],
        width: "50%"
    },
    badgeStyle: {
        color: "white",
        backgroundColor: green[500],
    },
    acceptButton: {
        position: "absolute",
        right: "1px",
    },
    headerIcon: {
        padding: 1,
        color: theme.mode === "light" ? "#0872B9" : "#FFF",
    },
    ticketQueueColor: {
        flex: "none",
        height: "100%",
        position: "absolute",
        top: "0%",
        left: "0%",
    },
    ticketInfo: {
        position: "relative",
        top: -13
    },
    secondaryContentSecond: {
        display: 'flex',
        alignItems: "flex-start",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignContent: "flex-start",
    },
    ticketInfo1: {
        position: "relative",
        top: 13,
        right: 0
    },
    Radiusdot: {
        "& .MuiBadge-badge": {
            borderRadius: 2,
            position: "inherit",
            height: 16,
            margin: 2,
            padding: 3
        },
        "& .MuiBadge-anchorOriginTopRightRectangle": {
            transform: "scale(1) translate(0%, -40%)",
        },
    },
    connectionIcon: {
        marginRight: theme.spacing(1)
    },
    selectionCheckOverlay: {
        position: "absolute",
        top: -2,
        left: -2,
        width: 24,
        height: 24,
        borderRadius: "50%",
        backgroundColor: theme.palette.primary.main,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10,
        fontSize: "0.8rem",
        border: "2px solid white",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    },
    avatarContainer: {
        position: "relative",
    },
    selectionMode: {
        cursor: "pointer",
        userSelect: "none",
    },
    contactSourceIcon: {
        position: "absolute",
        bottom: -2,
        left: -2,
        width: 20,
        height: 20,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 5,
        fontSize: "0.7rem",
        border: "2px solid white",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    },
    adminIcon: {
        position: "absolute",
        bottom: -2,
        left: -2,
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: "#FFD700", // Amarelo dourado
        color: "#000000", // Preto para contraste
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 5,
        fontSize: "0.7rem",
        border: "2px solid white",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    },
}));

const TicketListItemCustom = ({ setTabOpen, ticket, searchParam, searchOnMessages }) => {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [userTicketOpen, setUserTicketOpen] = useState("");
    const [queueTicketOpen, setQueueTicketOpen] = useState("");
    const { ticketId } = useParams();
    const isMounted = useRef(true);
    const { setCurrentTicket } = useContext(TicketsContext);
    const { user } = useContext(AuthContext);
    const { get: getSetting } = useCompanySettings();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [onModalConfirm, setOnModalConfirm] = useState(null);
    
    // Contexto de seleção múltipla
    const {
        isSelectionMode,
        selectTicket,
        isTicketSelected,
        enableSelectionMode,
    } = useBulkSelection();

    // Contexto de posição do scroll
    const { saveScrollPosition } = useScrollPosition();

    // Substituir o antigo AcceptTicketWithouSelectQueue pelo novo AcceptTicketModal
    const [acceptTicketModalOpen, setAcceptTicketModalOpen] = useState(false);


    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
        setAcceptTicketModalOpen(true);
    }, []);

    const handleCloseTicket = async (id) => {
        const setting = await getSetting({ "column": "requiredTag" });
        if (setting.requiredTag === "enabled") {
            try {
                const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
                if (!contactTags.data.tags) {
                    toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
                } else {
                    await api.put(`/tickets/${id}`, {
                        status: "closed",
                        userId: user?.id || null,
                    });
                    if (isMounted.current) {
                        setLoading(false);
                    }
                    history.push(`/tickets/`);
                }
            } catch (err) {
                setLoading(false);
                toastError(err);
            }
        } else {
            setLoading(true);
            try {
                await api.put(`/tickets/${id}`, {
                    status: "closed",
                    userId: user?.id || null,
                });
            } catch (err) {
                setLoading(false);
                toastError(err);
            }
            if (isMounted.current) {
                setLoading(false);
            }
            history.push(`/tickets/`);
        }
    };

    const handleCloseIgnoreTicket = async (id) => {
        setLoading(true);

        try {
            const setting = await getSetting({ column: "requiredTag" });

            if (setting.requiredTag === "enabled") {
                const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
                const hasTags = contactTags.data.tags && contactTags.data.tags.length > 0;

                //   if (!hasTags) {
                //     toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
                //     setLoading(false);
                //     return;
                //   }
            }

            await api.put(`/tickets/${id}`, {
                status: "archived",
                userId: user?.id || null,
                sendFarewellMessage: false,
                amountUsedBotQueues: 0,
            });

            history.push(`/tickets/`);
        } catch (err) {
            toastError(err);
        }

        setLoading(false);
    };


    const truncate = (str, len) => {
        if (!isNil(str)) {
            if (str.length > len) {
                return str.substring(0, len) + "...";
            }
            return str;
        }
    };

    const highlightSearchTerm = (text, searchTerm) => {
        if (!searchTerm || !text) return text;
        const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return text.replace(regex, '<strong style="background-color: #ffeb3b; padding: 1px 2px; border-radius: 2px;">$1</strong>');
    };

    const SearchResultsPreview = ({ messages, searchTerm }) => {
        if (!messages || messages.length === 0) return null;
        
        return (
            <div style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                {messages.slice(0, 2).map((message, index) => (
                    <div key={message.id} style={{ marginBottom: '2px' }}>
                        <span style={{ color: '#666', fontSize: '0.7rem' }}>
                            {message.fromMe ? 'Você' : 'Cliente'} - {format(parseISO(message.createdAt), "HH:mm")}:
                        </span>
                        <span 
                            dangerouslySetInnerHTML={{ 
                                __html: highlightSearchTerm(message.body, searchTerm) 
                            }} 
                            style={{ marginLeft: '4px' }}
                        />
                    </div>
                ))}
            </div>
        );
    };

    const handleCloseTransferTicketModal = useCallback(() => {
        if (isMounted.current) {
            setTransferTicketModalOpen(false);
        }
    }, []);

    const handleOpenTransferModal = () => {
        setLoading(true);
        setTransferTicketModalOpen(true);
        if (isMounted.current) {
            setLoading(false);
        }
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
    };

    const handleAcepptTicket = async (id) => {
        setLoading(true);
        try {
            const otherTicket = await api.put(`/tickets/${id}`, {
                status: ticket.isGroup && ticket.channel === 'whatsapp' ? "group" : "open",
                userId: user?.id,
            });
            if (otherTicket.data.id !== ticket.id) {
                if (otherTicket.data.userId !== user?.id) {
                    setOpenAlert(true);
                    setUserTicketOpen(otherTicket.data.user.name);
                    setQueueTicketOpen(otherTicket.data.queue.name);
                } else {
                    setLoading(false);
                    setTabOpen(ticket.isGroup ? "group" : "open");
                    handleSelectTicket(otherTicket.data);
                    history.push(`/tickets/${otherTicket.uuid}`);
                }
            } else {
                let setting;
                try {
                    setting = await getSetting({ "column": "sendGreetingAccepted" });
                } catch (err) {
                    toastError(err);
                }
                if (setting.sendGreetingAccepted === "enabled" && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")) {
                    handleSendMessage(ticket.id);
                }
                if (isMounted.current) {
                    setLoading(false);
                }
                setTabOpen(ticket.isGroup ? "group" : "open");
                handleSelectTicket(ticket);
                history.push(`/tickets/${ticket.uuid}`);
            }
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
    };

    const handleSendMessage = async (id) => {
        let setting;
        try {
            setting = await getSetting({ "column": "greetingAcceptedMessage" });
        } catch (err) {
            toastError(err);
        }
        const msg = `${setting.greetingAcceptedMessage}`;
        const message = {
            read: 1,
            fromMe: true,
            mediaUrl: "",
            body: `${msg.trim()}`,
        };
        try {
            await api.post(`/messages/${id}`, message);
        } catch (err) {
            toastError(err);
        }
    };

    const handleCloseAlert = useCallback(() => {
        setOpenAlert(false);
        setLoading(false);
    }, []);

    const handleSelectTicket = (ticket) => {
        const code = uuidv4();
        const { id, uuid } = ticket;
        setCurrentTicket({ id, uuid, code });
    };

    // Funções para seleção múltipla
    const handleTicketClick = (e) => {
        if (isSelectionMode) {
            e.preventDefault();
            e.stopPropagation();
            selectTicket(ticket);
        } else {
            // Salvar posição do scroll antes de navegar
            const scrollContainer = e.currentTarget.closest('.MuiPaper-root');
            if (scrollContainer) {
                const scrollTop = scrollContainer.scrollTop;
                const status = ticket.status || 'default';
                const currentSearchParam = searchParam || 'default';
                const scrollKey = `tickets-${status}-${currentSearchParam}`;
                saveScrollPosition(scrollKey, scrollTop);
            }
            
            handleSelectTicket(ticket);
            history.push(`/tickets/${ticket.uuid}`);
        }
    };

    const handleCheckboxClick = (e) => {
        e.stopPropagation();
        selectTicket(ticket);
    };

    const handleLongPress = () => {
        if ((user.profile === "admin" || user.allowBulkTicketActions === "enabled") && !isSelectionMode) {
            enableSelectionMode();
            selectTicket(ticket);
        }
    };

    // Para detectar long press
    const longPressTimer = useRef(null);
    const handleMouseDown = () => {
        if (user.profile === "admin" || user.allowBulkTicketActions === "enabled") {
            longPressTimer.current = setTimeout(handleLongPress, 500);
        }
    };

    const handleMouseUp = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    const handleMouseLeave = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };

    const handleOpenModalWithTagCheck = async () => {
        const setting = await getSetting({ "column": "requiredTag" });
        if (setting.requiredTag === "enabled") {
            try {
                const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
                if (!contactTags.data.tags) {
                    toast.warning(i18n.t("messagesList.header.buttons.requiredTagArchive"));
                } else {
                    setModalOpen(true);
                }
            } catch (err) {
                toastError(err);
            }
        } else {
            setModalOpen(true);
        }
    };



    return (
        <React.Fragment key={ticket.id}>
            {openAlert && (
                <ShowTicketOpen
                    isOpen={openAlert}
                    handleClose={handleCloseAlert}
                    user={userTicketOpen}
                    queue={queueTicketOpen}
                />
            )}
            {acceptTicketModalOpen && (
                <AcceptTicketModal
                    modalOpen={acceptTicketModalOpen}
                    onClose={() => setAcceptTicketModalOpen(false)}
                    ticket={ticket}
                />
            )}
            {transferTicketModalOpen && (
                <TransferTicketModalCustom
                    modalOpen={transferTicketModalOpen}
                    onClose={handleCloseTransferTicketModal}
                    ticketid={ticket.id}
                    ticket={ticket}
                />
            )}
            {modalOpen && (
                <CloseTicketModal
                    modalOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    ticket={ticket}
                    action="archive"
                />
            )}
            <ListItem
                button
                dense
                onClick={handleTicketClick}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                selected={ticketId && ticketId === ticket.uuid}
                className={clsx(classes.ticket, {
                    [classes.pendingTicket]: ticket.status === "pending",
                    [classes.selectionMode]: isSelectionMode,
                })}
                style={{
                    paddingLeft: 16,
                    paddingRight: 16,
                    alignItems: "center",
                    backgroundColor: isTicketSelected(ticket.id) ? "rgba(25, 118, 210, 0.12)" : "inherit",
                }}
            >


                <ListItemAvatar style={{ marginLeft: "0px"}}>
                    <div className={classes.avatarContainer}>
                        <Avatar
                            style={{
                                width: "50px",
                                height: "50px",
                                borderRadius: "50%",
                                objectFit: "cover",
                            }}
                            src={`${ticket?.contact?.profilePicUrl}`}
                        />
                        {/* Ícone de check sobreposto - apenas para admin em modo de seleção */}
                        {user.profile === "admin" && isSelectionMode && isTicketSelected(ticket.id) && (
                            <div className={classes.selectionCheckOverlay}>
                                <Check fontSize="small" />
                            </div>
                        )}
                        {/* Ícone da fonte de contato - apenas para fontes fixas */}
                        {ticket.contact?.contactSource?.isFixed && getSourceIcon(ticket.contact.contactSource.name) && (() => {
                            const sourceInfo = getSourceIcon(ticket.contact.contactSource.name);
                            const IconComponent = sourceInfo.icon;
                            
                            return (
                                <div 
                                    className={sourceInfo.isAdmin ? classes.adminIcon : classes.contactSourceIcon}
                                    style={!sourceInfo.isAdmin ? {
                                        backgroundColor: sourceInfo.bgColor,
                                        color: "white"
                                    } : {}}
                                >
                                    <IconComponent 
                                        style={{ 
                                            width: "12px", 
                                            height: "12px"
                                        }}
                                        color={sourceInfo.isAdmin ? "#000000" : "white"}
                                    />
                                </div>
                            );
                        })()}
                    </div>
                </ListItemAvatar>

                <ListItemText
                    disableTypography
                    primary={
                        <span className={classes.contactNameWrapper}>
                            <Typography noWrap component="span" variant="body2">
                                {ticket.isGroup && ticket.channel === "whatsapp" && (
                                    <GroupIcon fontSize="small" style={{ color: grey[700], marginBottom: '-1px', marginLeft: '5px' }} />
                                )}{" "}
                                &nbsp;
                                {ticket.channel && (
                                    <ConnectionIcon width="20" height="20" className={classes.connectionIcon} connectionType={ticket.channel} />
                                )}{" "}
                                &nbsp;
                                {truncate(ticket.contact?.name, 60)}
                                {ticket.contact?.campaignRandomizationId && (ticket.contact?.destinationWhatsappId || ticket.contact?.destinationUserId) && (
                                    <Tooltip title="Repasse de campanha" placement="top">
                                        <div
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                backgroundColor: '#f3e5f5',
                                                border: '1px solid #9c27b0',
                                                borderRadius: '12px',
                                                padding: '0px 6px',
                                                marginLeft: '4px',
                                                height: '18px'
                                            }}
                                        >
                                            <Shuffle
                                                style={{
                                                    color: '#9c27b0',
                                                    fontSize: '12px',
                                                    marginRight: '2px'
                                                }}
                                            />
                                            <span style={{ 
                                                color: '#9c27b0',
                                                fontSize: '11px',
                                                fontWeight: '500',
                                                lineHeight: '18px'
                                            }}>
                                                {ticket.contact?.destinationWhatsapp?.name || ticket.contact?.destinationUser?.name || ""}
                                            </span>
                                        </div>
                                    </Tooltip>
                                )}
                            </Typography>
                        </span>
                    }
                    secondary={
                        <span className={classes.contactNameWrapper}>
                            <Typography
                                className={Number(ticket.unreadMessages) > 0 ? classes.contactLastMessageUnread : classes.contactLastMessage}
                                noWrap
                                component="span"
                                variant="body2"
                            >
                                {/* Se há busca em mensagens e encontrou mensagens, mostrar preview */}
                                {searchOnMessages && searchParam && ticket.messages && ticket.messages.length > 0 ? (
                                    <SearchResultsPreview messages={ticket.messages} searchTerm={searchParam} />
                                ) : (
                                    <>
                                        {ticket.lastMessage ? (
                                            <>
                                                {ticket.lastMessage.includes("data:image/png;base64") ? (
                                                    <MarkdownWrapper>Localização</MarkdownWrapper>
                                                ) : (
                                                    <>
                                                        {ticket.lastMessage.includes("BEGIN:VCARD") ? (
                                                            <MarkdownWrapper>Contato</MarkdownWrapper>
                                                        ) : (
                                                            <MarkdownWrapper>{truncate(ticket.lastMessage, 40)}</MarkdownWrapper>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <br />
                                        )}
                                    </>
                                )}
                                <span className={classes.secondaryContentSecond}>
                                    {ticket?.whatsapp ? (
                                        <Badge
                                            className={classes.connectionTag}
                                            style={{
                                                backgroundColor:
                                                    ticket.channel === "whatsapp"
                                                        ? "#25D366"
                                                        : ticket.channel === "facebook"
                                                        ? "#4267B2"
                                                        : "#E1306C",
                                            }}
                                        >
                                            {ticket.whatsapp?.name.toUpperCase()}
                                        </Badge>
                                    ) : (
                                        <br />
                                    )}
                                    {ticket?.user && (
                                        <span className={classes.connectionTag}>
                                            {ticket.user?.name.toUpperCase()}
                                        </span>
                                    )}
                                </span>
                                <span className={classes.secondaryContentSecond}>
                                    {ticket.tags?.map((tag) => {
                                        return (
                                            <ContactTag tag={tag} key={`ticket-contact-tag-${ticket.id}-${tag.id}`} />
                                        );
                                    })}
                                </span>
                            </Typography>
                            {ticket.unreadMessages > 0 && (
                                <Badge
                                    className={classes.newMessagesCount}
                                    badgeContent={ticket.unreadMessages}
                                    classes={{
                                        badge: classes.badgeStyle,
                                    }}
                                />
                            )}
                        </span>
                    }
                />

                <ListItemSecondaryAction>
                    {ticket.lastMessage && (
                        <>
                            <Typography
                                className={Number(ticket.unreadMessages) > 0 ? classes.lastMessageTimeUnread : classes.lastMessageTime}
                                component="span"
                                variant="body2"
                            >
                                {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                                    <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
                                ) : (
                                    <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
                                )}
                            </Typography>
                            <br />
                        </>
                    )}
                </ListItemSecondaryAction>

                <ListItemSecondaryAction>
                    <span className={classes.secondaryContentSecond}>
                        {(ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined)) && (
                            <IconButton
                                className={`${classes.acceptButton} ${classes.headerIcon}`}
                                onClick={(e) => handleOpenAcceptTicketWithouSelectQueue()}
                                style={{ right: "51px", bottom: "-30px" }}
                            >
                                <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                                    <CheckCircleOutline />
                                </Tooltip>
                            </IconButton>
                        )}
                    </span>
                    <span className={classes.secondaryContentSecond}>
                        {(ticket.status === "pending" && ticket.queueId !== null) && (
                            <IconButton
                                className={`${classes.acceptButton} ${classes.headerIcon}`}
                                onClick={(e) => handleAcepptTicket(ticket.id)}
                                style={{ right: "51px", bottom: "-30px" }}
                            >
                                <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                                    <CheckCircleOutline />
                                </Tooltip>
                            </IconButton>
                        )}
                    </span>
                    <span className={classes.secondaryContentSecond}>
                        {(ticket.status === "pending" || ticket.status === "open" || ticket.status === "group") && (
                            <ButtonWithSpinner
                                style={{
                                    backgroundColor: "transparent",
                                    boxShadow: "none",
                                    border: "none",
                                    color: theme.mode === "light" ? "#0872B9" : "#FFF",
                                    padding: "0px",
                                    borderRadius: "50%",
                                    right: "26px",
                                    position: "absolute",
                                    fontSize: "0.6rem",
                                    bottom: "-30px",
                                    minWidth: "2em",
                                    width: "auto",
                                }}
                                variant="contained"
                                className={classes.acceptButton}
                                size="small"
                                loading={loading}
                                onClick={handleOpenTransferModal}
                            >
                                <Tooltip title={`${i18n.t("ticketsList.buttons.transfer")}`}>
                                    <SwapHoriz />
                                </Tooltip>
                            </ButtonWithSpinner>
                        )}
                    </span>
                    <span className={classes.secondaryContentSecond}>
                        {((ticket.status === "pending" || ticket.status === "lgpd") &&
                            (user.userClosePendingTicket === "enabled" || user.profile === "admin")) && (
                            <IconButton
                                className={`${classes.acceptButton} ${classes.headerIcon}`}
                                onClick={handleOpenModalWithTagCheck}
                                style={{ right: "1px", bottom: "-30px" }}
                            >
                                <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                                    <ArchiveOutlined />
                                </Tooltip>
                            </IconButton>
                        )}
                    </span>
                    <span className={classes.secondaryContentSecond}>
                        {(ticket.status === "closed" && (ticket.queueId === null || ticket.queueId === undefined)) && (
                            <ButtonWithSpinner
                                style={{
                                    backgroundColor: "transparent",
                                    boxShadow: "none",
                                    border: "none",
                                    color: theme.mode === "light" ? "#0872B9" : "#FFF",
                                    padding: "0px",
                                    bottom: "0px",
                                    borderRadius: "50%",
                                    right: "1px",
                                    fontSize: "0.6rem",
                                    bottom: "-30px",
                                    minWidth: "2em",
                                    width: "auto",
                                }}
                                variant="contained"
                                className={classes.acceptButton}
                                size="small"
                                loading={loading}
                                onClick={(e) => handleOpenAcceptTicketWithouSelectQueue()}
                            >
                                <Tooltip title={`${i18n.t("ticketsList.buttons.reopen")}`}>
                                    <Replay />
                                </Tooltip>
                            </ButtonWithSpinner>
                        )}
                    </span>
                    <span className={classes.secondaryContentSecond}>
                        {(ticket.status === "closed" && ticket.queueId !== null) && (
                            <ButtonWithSpinner
                                style={{
                                    backgroundColor: "transparent",
                                    boxShadow: "none",
                                    border: "none",
                                    color: theme.mode === "light" ? "#0872B9" : "#FFF",
                                    padding: "0px",
                                    bottom: "0px",
                                    borderRadius: "50%",
                                    right: "1px",
                                    fontSize: "0.6rem",
                                    bottom: "-30px",
                                    minWidth: "2em",
                                    width: "auto",
                                }}
                                variant="contained"
                                className={classes.acceptButton}
                                size="small"
                                loading={loading}
                                onClick={(e) => handleAcepptTicket(ticket.id)}
                            >
                                <Tooltip title={`${i18n.t("ticketsList.buttons.reopen")}`}>
                                    <Replay />
                                </Tooltip>
                            </ButtonWithSpinner>
                        )}
                    </span>
                    <span className={classes.secondaryContentSecond}>
                        {(ticket.status === "open" || ticket.status === "group") && (
                            <IconButton
                                className={`${classes.acceptButton} ${classes.headerIcon}`}
                                onClick={handleOpenModalWithTagCheck}
                                style={{ right: "1px", bottom: "-30px" }}
                            >
                                <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                                    <ArchiveOutlined />
                                </Tooltip>
                            </IconButton>
                        )}
                    </span>
                </ListItemSecondaryAction>
            </ListItem>
        </React.Fragment>
    );
};

export default TicketListItemCustom;
